<template>
  <table
    :class="[
      'tm-table',
      {
        'tm-table--has-selected-rows': hasSelectedRows,
        'tm-table--single-selection': singleSelection,
        'tm-table--fixed-layout': fixedLayout,
        'tm-table--loading': isLoading,
        'tm-table--new-sizing': isNewSizingFlowEnabled,
        'tm-table--theme-borderless': theme === TableTheme.BORDERLESS,
      },
    ]"
  >
    <thead>
      <slot name="table-header" />
      <slot
        v-if="isLoading"
        name="table-progress"
      >
        <tr class="tm-table-progress">
          <th colspan="100%">
            <div class="tm-table-progress-bar">
              <div class="tm-table-progress-bar__value" />
            </div>
          </th>
        </tr>
      </slot>
    </thead>
    <slot name="table-body">
      <tbody>
        <slot name="selected-rows" />
        <slot name="rows" />
      </tbody>
    </slot>
  </table>
</template>

<script setup lang="ts">
import { TableTheme } from '@/components/shared/table/types'

defineProps<{
  isLoading?: boolean
  hasSelectedRows?: boolean
  singleSelection?: boolean
  fixedLayout?: boolean
  isNewSizingFlowEnabled?: boolean
  theme?: TableTheme
}>()
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import '@/styles/mixins.scss';

.tm-table {
  width: 100%;
  max-width: 100%;
  border-spacing: 0;

  &--new-sizing {
    width: auto;
    max-width: unset;
    table-layout: fixed;
  }

  &.tm-table--has-selected-rows:not(.tm-table--single-selection) {
    :deep(tbody) {
      tr:first-child {
        td {
          will-change: border-top;
          border-top: thin solid $border-color;
        }
      }

      &::before {
        display: block;
        content: '';
        height: $tmRowSelectedStickyContainerHeight;
        margin-bottom: -1px; // to compensate for the border-top of the first row
      }
    }
  }

  &.tm-table--fixed-layout {
    table-layout: fixed;
  }

  :deep(td) {
    height: var(--tm-table-row-height);
    padding: 0 10px;
    font-size: $font-size-root;
    line-height: $font-size-root + math.round(0.15 * $font-size-root);
    font-weight: $regular-font;
    overflow: hidden;
    white-space: nowrap;
  }

  :deep(th) {
    height: var(--tm-table-header-height);
    padding: 0 10px;
    font-size: $font-size-root;
    line-height: $font-size-root + math.round(0.15 * $font-size-root);
    font-weight: $regular-font;
    overflow: hidden;
    white-space: nowrap;
  }

  :deep(thead) {
    background-color: var(--tm-table-header-bg, $shade);

    tr {
      height: auto;
    }

    th {
      color: var(--tm-table-header-text-color, $emphasize);
      text-align: left;
      font-size: $sm-x-font;
      font-weight: $bold-font;
      border-bottom: var(--tm-table-border-width) solid $border-color;
    }
  }

  :deep(tbody) {
    tr {
      td {
        will-change: background-color;
      }

      // Disable bottom border for last row
      &:not(:last-child) {
        td {
          border-bottom: var(--tm-table-border-width) solid var(--tm-table-border-color, $border-color);
        }
      }

      // Highlight the first column of the row, excluding the checkbox column
      & > td {
        &:not(.tm-table-checkbox):first-child,
        &.tm-table-checkbox:first-child + td {
          font-weight: var(--tm-table-first-column-font-weight);
        }
      }

      &:hover {
        &:not(.alert-tr) {
          td {
            background-color: $background-blue;
          }
        }
      }
    }
    td {
      position: relative;
      // to truncate cells properly
      max-width: 40px;
    }
    td.row-selected,
    td.row-selected ~ td {
      background: $background-blue;
    }
  }

  :deep(.tm-checkbox),
  :deep(.tm-table-checkbox) {
    width: 38px;
    min-width: 38px;
  }
  :deep(.td-actions) {
    width: 52px;
    min-width: 52px;
    max-width: 52px;
  }
}

.tm-table--theme-borderless {
  --tm-table-header-bg: #{$white};
  --tm-table-header-text-color: #{$distinct};
  --tm-table-row-height: 48px;

  :deep(thead) {
    tr {
      th {
        &:first-child {
          padding-left: 0;
        }
      }
    }
  }

  :deep(tbody) {
    tr {
      td {
        border-bottom: var(--tm-table-border-width) solid var(--tm-table-border-color, $border-color);

        &:first-child {
          padding-left: 0;
        }
      }
    }
  }
}

.tm-table-progress {
  position: sticky;
  z-index: 2;
  height: 0;

  thead & {
    th {
      height: 0;
      border: none;
      padding: 0;
      position: relative;
      overflow: visible;
    }
  }

  .tm-table-progress-bar {
    position: absolute;
    bottom: 0;
    height: 2px;
    width: 100%;
    overflow: hidden;
    transform: translateY(25%);

    &__value {
      background-color: $blue500;
      width: calc(100% / 3);
      height: 100%;
      animation: indeterminateProgress 1.5s infinite linear;
      transform-origin: 0 100%;
    }
  }
}

@keyframes indeterminateProgress {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(300%);
  }
}
</style>
